import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuBot, LuExpand, LuLink, LuLoaderPinwheel, LuSend, LuTrash2 } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { StandaloneChangelogEntry } from '../../entities/standalone-changelog';
import { StandaloneComment } from '../../entities/standalone-comment';
import { StandaloneProject } from '../../entities/standalone-project';
import { StandaloneTask, StandaloneTaskPriority } from '../../entities/standalone-task';
import {
  createStandaloneComment,
  deleteStandaloneComment,
  listStandaloneChangelog,
  listStandaloneComments,
  listStandaloneSubtasks,
  standaloneErrorMessage,
} from '../../shared/api/standalone';
import { auth } from '../../shared/firebase/firebase';
import useSocket from '../../shared/hooks/socket-hook';
import { AgentRoleWarning, getAgentRoleWarning } from '../../shared/utils/agent-role';
import { contrastTextColor } from '../../shared/utils/color';
import { collectDescendantIds } from '../../shared/utils/standalone-task-tree';
import MarkdownRenderer from '../MarkdownRenderer';
import { AgentAuthorBadge } from '../ui/AgentAuthorBadge';
import { MarkdownField } from '../ui/MarkdownField';
import { Modal } from '../ui/Modal';
import { AgentRoleWarningModal } from './AgentRoleWarningModal';
import { AttachmentsPanel } from './AttachmentsPanel';
import { ChangelogFeed } from './ChangelogFeed';

export interface TaskFormValues {
  title: string;
  description?: string;
  priority?: StandaloneTaskPriority;
  dueDate?: string;
  status: string;
  parentId?: string;
  labelIds?: string[];
}

interface TaskModalProps {
  project: StandaloneProject;
  tasks: StandaloneTask[]; // full flat list — used to build the parent-task picker and exclude cycles
  task?: StandaloneTask | null; // present = edit mode
  defaultStatus: string;
  onClose: () => void;
  onSave: (values: TaskFormValues) => Promise<void>;
  onDelete?: () => Promise<void>;
  onQuickAddSubtask?: (title: string) => Promise<void>; // only offered in edit mode
}

const TaskModal: React.FC<TaskModalProps> = ({
  project,
  tasks,
  task,
  defaultStatus,
  onClose,
  onSave,
  onDelete,
  onQuickAddSubtask,
}) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [priority, setPriority] = useState<StandaloneTaskPriority | ''>(task?.priority ?? '');
  const [dueDate, setDueDate] = useState(task?.dueDate ? task.dueDate.slice(0, 10) : '');
  const [status, setStatus] = useState<string>(task?.status ?? defaultStatus);
  const [parentId, setParentId] = useState<string>(task?.parentId ?? '');
  const [labelIds, setLabelIds] = useState<string[]>(task?.labelIds ?? []);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [isAddingSubtask, setIsAddingSubtask] = useState(false);
  const [fetchedSubtasks, setFetchedSubtasks] = useState<StandaloneTask[]>([]);
  const [isLoadingSubtasks, setIsLoadingSubtasks] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activityTab, setActivityTab] = useState<'comments' | 'activity' | 'attachments'>('comments');
  const [comments, setComments] = useState<StandaloneComment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [changelog, setChangelog] = useState<StandaloneChangelogEntry[]>([]);
  const [isLoadingChangelog, setIsLoadingChangelog] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);
  const [agentRoleWarning, setAgentRoleWarning] = useState<AgentRoleWarning | null>(null);
  const { on, off } = useSocket(auth.currentUser?.uid || '');

  const statuses = useMemo(() => [...project.statuses].sort((a, b) => a.order - b.order), [project.statuses]);

  const excludedParentIds = useMemo(() => {
    if (!task) return new Set<string>();
    return new Set([task._id, ...collectDescendantIds(task._id, tasks)]);
  }, [task, tasks]);

  const parentOptions = tasks.filter((t) => !excludedParentIds.has(t._id));

  const toggleLabel = (labelId: string) => {
    setLabelIds((prev) => (prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]));
  };

  useEffect(() => {
    if (!task) return;
    setIsLoadingSubtasks(true);
    listStandaloneSubtasks(task.projectId, task._id)
      .then(setFetchedSubtasks)
      .catch(() => {})
      .finally(() => setIsLoadingSubtasks(false));
  }, [task]);

  // This modal is always remounted fresh per task (see StandaloneBoardPage), so a plain
  // mount-only fetch is enough — no task-switch-in-place case to guard against.
  useEffect(() => {
    if (!task) return;
    setIsLoadingComments(true);
    listStandaloneComments(task._id)
      .then(setComments)
      .catch(() => {})
      .finally(() => setIsLoadingComments(false));
  }, [task]);

  // Read-only, no live socket event in the BE contract (MVPB-25) — loaded once on open.
  useEffect(() => {
    if (!task) return;
    setIsLoadingChangelog(true);
    listStandaloneChangelog(task._id)
      .then(setChangelog)
      .catch(() => {})
      .finally(() => setIsLoadingChangelog(false));
  }, [task]);

  // Live sync — the chat agent can also add comments via its own tool call
  // (standalone-contract.md "Comments"). Dedupe-safe in case the WS echo beats the REST response.
  useEffect(() => {
    if (!task) return;
    const onCommentAdded = (data: { userId: string; comment: StandaloneComment }) => {
      if (data.comment.taskId !== task._id) return;
      setComments((prev) => (prev.some((c) => c._id === data.comment._id) ? prev : [...prev, data.comment]));
    };
    on('standaloneCommentAdded', onCommentAdded);
    return () => off('standaloneCommentAdded', onCommentAdded as (...args: unknown[]) => void);
  }, [task, on, off]);

  const handleAddComment = async () => {
    if (!task || !commentText.trim()) return;
    setIsAddingComment(true);
    setError(null);
    try {
      const created = await createStandaloneComment(task._id, commentText.trim());
      setComments((prev) => (prev.some((c) => c._id === created._id) ? prev : [...prev, created]));
      setCommentText('');
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add comment.');
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!task) return;
    setDeletingCommentId(commentId);
    setError(null);
    try {
      await deleteStandaloneComment(task._id, commentId);
      setComments((prev) => prev.filter((c) => c._id !== commentId));
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete comment.');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        priority: priority || undefined,
        dueDate: dueDate || undefined,
        status,
        parentId: parentId || undefined,
        labelIds,
      });
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to save task.');
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!window.confirm('Delete this task?')) return;
    setIsDeleting(true);
    setError(null);
    try {
      await onDelete();
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to delete task.');
      setIsDeleting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!task) return;
    const url = `${window.location.origin}/standalone/tasks/${task.key}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleOpenFullPage = () => {
    if (!task) return;
    navigate(`/standalone/tasks/${task.key}`);
    onClose();
  };

  const goToAgent = () => {
    if (!task) return;
    navigate('/coding-agent', {
      state: {
        standaloneTaskId: task._id,
        taskKey: task.key,
        prompt: task.description ? `${task.title}\n\n${task.description}` : task.title,
      },
    });
    onClose();
  };

  const handleAssignToAgent = () => {
    if (!task) return;
    const warning = getAgentRoleWarning(project);
    if (warning) {
      setAgentRoleWarning(warning);
      return;
    }
    goToAgent();
  };

  const handleAddSubtask = async () => {
    if (!subtaskTitle.trim() || !onQuickAddSubtask) return;
    setIsAddingSubtask(true);
    setError(null);
    try {
      await onQuickAddSubtask(subtaskTitle.trim());
      setSubtaskTitle('');
      if (task) {
        const subs = await listStandaloneSubtasks(task.projectId, task._id);
        setFetchedSubtasks(subs);
      }
    } catch (err) {
      setError(standaloneErrorMessage(err) ?? 'Failed to add subtask.');
    } finally {
      setIsAddingSubtask(false);
    }
  };

  return (
    <>
      {agentRoleWarning && (
        <AgentRoleWarningModal
          projectId={project._id}
          warning={agentRoleWarning}
          onCancel={() => setAgentRoleWarning(null)}
          onContinue={() => {
            setAgentRoleWarning(null);
            goToAgent();
          }}
        />
      )}
      <Modal onClose={onClose} className="max-w-2xl max-h-[85vh] overflow-y-auto" labelledBy="task-modal-title">
        <div className="card-body p-6 gap-4">
          <div className="flex items-center justify-between gap-2">
            <div id="task-modal-title" className="text-lg font-semibold flex items-center gap-2">
              {task ? 'Edit task' : 'New task'}
              {task && <span className="font-mono text-xs opacity-40">{task.key}</span>}
              {task && task.authorSource === 'mcp_agent' && <AgentAuthorBadge clientName={task.clientName} />}
            </div>
            {task && (
              <div className="flex items-center gap-1">
                <button className="btn btn-ghost btn-xs gap-1" onClick={handleCopyLink} title="Copy link">
                  <LuLink size={13} /> Copy link
                </button>
                <button className="btn btn-ghost btn-xs gap-1" onClick={handleOpenFullPage} title="Open full page">
                  <LuExpand size={13} /> Open full page
                </button>
                <button
                  className="btn btn-ghost btn-xs gap-1"
                  onClick={handleAssignToAgent}
                  title="Assign to coding agent"
                >
                  <LuBot size={13} /> Assign to agent
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="alert alert-error py-2">
              <span className="text-sm">{error}</span>
            </div>
          )}

          <input
            className="input input-bordered input-lg w-full text-base"
            placeholder="Task title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <MarkdownField
            value={description}
            onChange={setDescription}
            placeholder="Description (optional) — Markdown supported"
            rows={5}
          />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="form-control">
              <span className="label-text text-xs mb-1">Status</span>
              <select
                className="select select-bordered select-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {statuses.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="form-control">
              <span className="label-text text-xs mb-1">Priority</span>
              <select
                className="select select-bordered select-sm"
                value={priority}
                onChange={(e) => setPriority(e.target.value as StandaloneTaskPriority | '')}
              >
                <option value="">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>

            <label className="form-control">
              <span className="label-text text-xs mb-1">Due date</span>
              <input
                type="date"
                className="input input-bordered input-sm"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </label>

            {parentOptions.length > 0 && (
              <label className="form-control">
                <span className="label-text text-xs mb-1">Parent task</span>
                <select
                  className="select select-bordered select-sm"
                  value={parentId}
                  onChange={(e) => setParentId(e.target.value)}
                >
                  <option value="">None (root task)</option>
                  {parentOptions.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {project.labels.length > 0 && (
            <div className="form-control">
              <span className="label-text text-xs mb-1">Labels</span>
              <div className="flex flex-wrap gap-1.5">
                {project.labels.map((label) => {
                  const active = labelIds.includes(label.id);
                  return (
                    <button
                      key={label.id}
                      type="button"
                      className={`badge badge-sm cursor-pointer ${active ? '' : 'badge-outline opacity-60'}`}
                      style={
                        active
                          ? {
                              backgroundColor: label.color || '#6b7280',
                              borderColor: label.color || '#6b7280',
                              color: contrastTextColor(label.color),
                            }
                          : undefined
                      }
                      onClick={() => toggleLabel(label.id)}
                    >
                      {label.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {task && (
            <div className="form-control">
              <span className="label-text text-xs mb-1">
                Subtasks {fetchedSubtasks.length > 0 && `(${fetchedSubtasks.length})`}
                {isLoadingSubtasks && <span className="loading loading-spinner loading-xs ml-2 opacity-50" />}
              </span>
              {fetchedSubtasks.length > 0 && (
                <div className="space-y-1 mb-1.5 max-h-40 overflow-y-auto">
                  {fetchedSubtasks.map((sub) => {
                    const subStatus = statuses.find((s) => s.id === sub.status);
                    return (
                      <button
                        key={sub._id}
                        type="button"
                        className="w-full flex items-center gap-2 text-sm bg-base-200 rounded-lg px-2.5 py-1.5 text-left hover:bg-base-300 transition"
                        onClick={() => {
                          navigate(`/standalone/tasks/${sub.key}`);
                          onClose();
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full shrink-0"
                          style={{ backgroundColor: subStatus?.color || '#9ca3af' }}
                        />
                        <span className="font-mono text-xs opacity-50 shrink-0">{sub.key}</span>
                        <span className="truncate flex-1">{sub.title}</span>
                        <span className="text-xs opacity-60 shrink-0">{subStatus?.name || 'Unknown'}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {onQuickAddSubtask && (
                <div className="flex items-center gap-2">
                  <input
                    className="input input-bordered input-sm flex-1"
                    placeholder="Add a subtask…"
                    value={subtaskTitle}
                    onChange={(e) => setSubtaskTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddSubtask()}
                  />
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleAddSubtask}
                    disabled={!subtaskTitle.trim() || isAddingSubtask}
                  >
                    {isAddingSubtask && <LuLoaderPinwheel className="animate-spin" size={14} />}
                    Add
                  </button>
                </div>
              )}
            </div>
          )}

          {task && (
            <div className="form-control">
              <div className="flex items-center gap-1 mb-1.5">
                <button
                  type="button"
                  className={`btn btn-xs ${activityTab === 'comments' ? 'btn-active' : 'btn-ghost'}`}
                  onClick={() => setActivityTab('comments')}
                >
                  Comments {comments.length > 0 && `(${comments.length})`}
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${activityTab === 'activity' ? 'btn-active' : 'btn-ghost'}`}
                  onClick={() => setActivityTab('activity')}
                >
                  Activity {changelog.length > 0 && `(${changelog.length})`}
                </button>
                <button
                  type="button"
                  className={`btn btn-xs ${activityTab === 'attachments' ? 'btn-active' : 'btn-ghost'}`}
                  onClick={() => setActivityTab('attachments')}
                >
                  Attachments
                </button>
              </div>

              {activityTab === 'activity' ? (
                <ChangelogFeed entries={changelog} isLoading={isLoadingChangelog} />
              ) : activityTab === 'attachments' ? (
                <AttachmentsPanel taskId={task._id} />
              ) : (
                <>
                  {isLoadingComments ? (
                    <div className="flex justify-center py-2">
                      <LuLoaderPinwheel className="animate-spin opacity-40" size={16} />
                    </div>
                  ) : (
                    comments.length > 0 && (
                      <div className="space-y-1.5 mb-1.5 max-h-48 overflow-y-auto">
                        {comments.map((comment) => (
                          <div key={comment._id} className="rounded-lg bg-base-200 px-3 py-2 text-sm space-y-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5">
                                {comment.authorType === 'agent' && (
                                  <span className="badge badge-primary badge-xs gap-1">
                                    <LuBot size={10} /> mFlow
                                  </span>
                                )}
                                {comment.authorSource === 'mcp_agent' && (
                                  <AgentAuthorBadge clientName={comment.clientName} />
                                )}
                                <span className="text-xs opacity-40">
                                  {new Date(comment.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <button
                                className="btn btn-ghost btn-xs btn-circle"
                                onClick={() => handleDeleteComment(comment._id)}
                                disabled={deletingCommentId === comment._id}
                                title="Delete comment"
                              >
                                {deletingCommentId === comment._id ? (
                                  <span className="loading loading-spinner loading-xs" />
                                ) : (
                                  <LuTrash2 size={12} />
                                )}
                              </button>
                            </div>
                            <MarkdownRenderer markdown={comment.text} />
                          </div>
                        ))}
                      </div>
                    )
                  )}
                  <div className="flex flex-col gap-2">
                    <MarkdownField
                      value={commentText}
                      onChange={setCommentText}
                      placeholder="Add a comment… (Markdown supported, ⌘+Enter to send)"
                      rows={2}
                      className="textarea textarea-bordered textarea-sm w-full"
                      onSubmitShortcut={handleAddComment}
                    />
                    <div className="flex justify-end">
                      <button
                        className="btn btn-outline btn-sm gap-1.5"
                        onClick={handleAddComment}
                        disabled={!commentText.trim() || isAddingComment}
                      >
                        {isAddingComment ? (
                          <LuLoaderPinwheel className="animate-spin" size={14} />
                        ) : (
                          <LuSend size={14} />
                        )}
                        Comment
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <div>
              {task && onDelete && (
                <button className="btn btn-ghost btn-sm text-error" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting && <LuLoaderPinwheel className="animate-spin" size={14} />}
                  Delete
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={isSaving}>
                Cancel
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={isSaving || !title.trim()}>
                {isSaving && <LuLoaderPinwheel className="animate-spin" size={14} />}
                Save
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TaskModal;
